/**
 * Shared formatting utilities
 * Centralizes date, number, and text formatting functions
 */

/**
 * Format a date string or Date object to a readable format
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  const {
    includeTime = false,
    relative = false,
    short = false
  } = options;
  
  // Handle MongoDB date format {"$date": "..."} 
  let dateValue = dateInput;
  if (typeof dateInput === 'object' && dateInput.$date) {
    dateValue = dateInput.$date;
  }
  
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  
  if (isNaN(date.getTime())) return '';
  
  // Relative time (e.g., "2 hours ago")
  if (relative) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
  }
  
  // Short format (e.g., "Jan 15")
  if (short) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  
  // Full format
  const dateOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric'
  };
  
  if (includeTime) {
    return date.toLocaleDateString('en-US', {
      ...dateOptions,
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return date.toLocaleDateString('en-US', dateOptions);
};

/**
 * Format a number with thousands separators
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: GBP)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'GBP') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency
  }).format(amount);
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Generate URL-friendly slug
 * @param {string} text - Text to slugify
 * @returns {string} URL-friendly slug
 */
export const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text.replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Alias for slugify - for backwards compatibility
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
export const generateSlug = slugify;

/**
 * Generate the canonical URL path for a product group
 * Format: /product/{category}/{subcategory}/{slug}
 * @param {object} product - Product group object with category, subcategory, slug, and title
 * @returns {string} Canonical URL path
 */
export const getProductCanonicalUrl = (product) => {
  if (!product) return '/search';
  
  const slug = product.slug || generateSlug(product.title || '');
  const category = product.category || '';
  const subcategory = product.subcategory || '';
  
  // Build the canonical path: /product/category/subcategory/slug
  if (category && subcategory && slug) {
    return `/product/${category}/${subcategory}/${slug}`;
  } else if (category && slug) {
    return `/product/${category}/${slug}`;
  }
  
  // Fallback to just slug
  return `/product/${slug}`;
};

// ============== MARKDOWN/TEXT FORMATTING ==============

/**
 * Parse markdown-style formatting to HTML
 * @param {string} text - Text with markdown formatting
 * @returns {string} HTML string
 */
export const parseFormatting = (text) => {
  if (!text) return '';
  return text
    .replace(/\n/g, '<br />')  // Line breaks
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')  // Bold
    .replace(/\*(.+?)\*/g, '<em>$1</em>');  // Italic
};

/**
 * React component to render formatted text
 */
export const FormattedText = ({ children, className = '' }) => {
  if (!children) return null;
  return (
    <span 
      className={className}
      dangerouslySetInnerHTML={{ __html: parseFormatting(children) }}
    />
  );
};
